import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

/**
 * Admin Mode — global toggle that lets band admins switch between
 * "member view" (the default — what a regular player sees) and
 * "admin view" (full management UI). For non-admin users it's a no-op:
 * `showAdminControls` is always false.
 *
 * Why: admins are players first. The clutter of management controls on every
 * page hides the day-to-day "what's coming up / what should I bring" info
 * that even admins use most often.
 *
 * Persistence: stored in localStorage so the choice survives page refreshes.
 *
 * Usage in any page:
 *   const { showAdminControls } = useAdminControls();
 *   {showAdminControls && <Button>Create Event</Button>}
 */

const AdminModeContext = createContext({ adminMode: false, toggle: () => {}, showAdminControls: false });

const STORAGE_KEY = "vivo:admin_mode";

export const AdminModeProvider = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Default OFF for admins (member-first view). For non-admins this value
  // is irrelevant — gated by isAdmin below.
  const [adminMode, setAdminMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, adminMode ? "1" : "0");
    } catch {
      /* ignore quota errors */
    }
  }, [adminMode]);

  // If the user is no longer an admin (e.g. role change, signed out),
  // forcibly turn the mode off so stale state can't leak through.
  useEffect(() => {
    if (!isAdmin && adminMode) setAdminMode(false);
  }, [isAdmin, adminMode]);

  const toggle = useCallback(() => setAdminMode((m) => !m), []);

  const showAdminControls = isAdmin && adminMode;

  return (
    <AdminModeContext.Provider value={{ adminMode, toggle, showAdminControls, isAdmin }}>
      {children}
    </AdminModeContext.Provider>
  );
};

export const useAdminControls = () => useContext(AdminModeContext);
